import * as React from 'react';

import type * as api from '@gitbook/api-client';

type BackgroundAgentsContextValue = {
    /** The background agents the conversation has started, as the agent last reported them. */
    agents: api.AgentBackgroundAgent[];
    /** Open a background agent's own conversation, when the surface has somewhere to open it. */
    onOpen?: (agent: api.AgentBackgroundAgent) => void;
};

const BackgroundAgentsContext = React.createContext<BackgroundAgentsContextValue>({ agents: [] });

/**
 * Publish the conversation's background agents to the transcript below it.
 *
 * The tool call that started one never changes, while what the agent is doing is live — so the card
 * reads its status from here, and a transcript rendered without this context just shows the task.
 */
export function BackgroundAgentsProvider(
    props: React.PropsWithChildren<BackgroundAgentsContextValue>
) {
    const { agents, onOpen, children } = props;

    const value = React.useMemo(() => ({ agents, onOpen }), [agents, onOpen]);

    return (
        <BackgroundAgentsContext.Provider value={value}>
            {children}
        </BackgroundAgentsContext.Provider>
    );
}

/** Whether this conversation has delegated anything, which is what a runtime message means here. */
export function useHasBackgroundAgents(): boolean {
    return React.useContext(BackgroundAgentsContext).agents.length > 0;
}

/** The background agent behind a tool call, and how to open it. Null if the state has neither. */
export function useBackgroundAgent(agentId: string): {
    agent: api.AgentBackgroundAgent | null;
    onOpen: (() => void) | undefined;
} {
    const { agents, onOpen } = React.useContext(BackgroundAgentsContext);
    const agent = agents.find((candidate) => candidate.id === agentId) ?? null;

    return {
        agent,
        onOpen: agent && onOpen ? () => onOpen(agent) : undefined,
    };
}
